/**
 * Beckhoff XTS coordinate-convention transform.
 *
 * The Beckhoff XTS `OriginTransform` parameter uses a Y-axis sign that is
 * opposite to the viewer's right-handed Z-up world frame. When
 * `coordinateSystem === 'beckhoff-xts'`, this module negates:
 *
 *   - `PartTransformation.offsetMm[1]`        (Y translation)
 *   - `PartTransformation.rotationDegEuler[2]` (Z-Euler rotation)
 *   - `TrackTransform.positionMm[1]`           (Y translation)
 *   - `TrackTransform.rotationDegEuler[2]`     (Z-Euler rotation)
 *
 * Applied as a post-normalization pass so every downstream consumer
 * (rendering, imperative API, bounding-box) sees the corrected values.
 */

import type { NormalizedXtsConfig } from './normalizeXtsConfig.js';
import type {
  PartTransformation,
  TrackTransform,
  Vec3,
} from '../types.js';

const negateY = ([x, y, z]: Vec3): Vec3 => [x, -y, z];
const negateZEuler = ([rx, ry, rz]: Vec3): Vec3 => [rx, ry, -rz];

function flipPartTransformation(
  pt: PartTransformation | undefined,
): PartTransformation | undefined {
  if (!pt) return pt;
  const out: PartTransformation = { ...pt };
  if (pt.offsetMm) out.offsetMm = negateY(pt.offsetMm);
  if (pt.rotationDegEuler) out.rotationDegEuler = negateZEuler(pt.rotationDegEuler);
  return out;
}

function flipTrackTransform(
  tt: TrackTransform | undefined,
): TrackTransform | undefined {
  if (!tt) return tt;
  const out: TrackTransform = { ...tt };
  if (tt.positionMm) out.positionMm = negateY(tt.positionMm);
  if (tt.rotationDegEuler) out.rotationDegEuler = negateZEuler(tt.rotationDegEuler);
  return out;
}

export function applyBeckhoffXtsConvention(
  config: NormalizedXtsConfig,
): NormalizedXtsConfig {
  return {
    ...config,
    processingUnits: config.processingUnits.map((xpu) => ({
      ...xpu,
      trackTransform: flipTrackTransform(xpu.trackTransform),
      parts: xpu.parts.map((part) => ({
        ...part,
        partTransformation: flipPartTransformation(part.partTransformation),
      })),
    })),
  };
}
